import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { getMediumRole } from '../../../utils/utils';

var avatarPalettes = [
  { background: '#f5e6d8', color: '#b87040' },
  { background: '#faf0e8', color: '#c07838' },
  { background: '#eeddd0', color: '#a86030' },
  { background: '#f7ece0', color: '#d5a67b' },
  { background: '#f0e0cc', color: '#b87848' },
];

function getInitials(name) {
  if (!name) return '?';
  var parts = name.trim().split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function getAvatarStyle(name) {
  var idx = name ? name.charCodeAt(0) % avatarPalettes.length : 0;
  return avatarPalettes[idx];
}

function getStatusLabel(statusName) {
  var s = parseInt(statusName);
  if (s === 1) return { label: 'Approved', color: '#27ae60', bg: '#eafaf1' };
  if (s === 2) return { label: 'Declined', color: '#e74c3c', bg: '#fdf0ef' };
  if (s === 4) return { label: 'Restricted', color: '#e67e22', bg: '#fef5ec' };
  return { label: 'Pending', color: '#888', bg: '#f5f5f5' };
}

function getButtonStyle(variant) {
  var base = {
    padding: '8px 14px',
    fontSize: '12px',
    fontWeight: '600',
    borderRadius: '6px',
    cursor: 'pointer',
    border: '1px solid transparent',
    whiteSpace: 'nowrap',
  };
  if (variant === 'primary') return Object.assign({}, base, { background: '#d5a67b', color: '#fff', borderColor: '#d5a67b' });
  if (variant === 'danger') return Object.assign({}, base, { background: '#fff', color: '#e74c3c', borderColor: '#e74c3c' });
  if (variant === 'success') return Object.assign({}, base, { background: '#27ae60', color: '#fff', borderColor: '#27ae60' });
  if (variant === 'warning') return Object.assign({}, base, { background: '#e67e22', color: '#fff', borderColor: '#e67e22' });
  return Object.assign({}, base, { background: '#f5f0eb', color: '#485257', borderColor: '#e8ddd6' });
}

function InfoRow(props) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '9px 0', borderBottom: '1px solid #f0ebe5', gap: '12px' }}>
      <span style={{ fontSize: '12px', color: '#999', fontWeight: '500', flexShrink: 0, minWidth: '120px' }}>{props.label}</span>
      <span style={{ fontSize: '13px', color: '#485257', fontWeight: '500', textAlign: 'right', wordBreak: 'break-word' }}>{props.value || '-'}</span>
    </div>
  );
}

export default function MediumInfoPanel(props) {
  var medium = props.medium;
  var details = props.details;
  var detailsLoading = props.detailsLoading;
  var onClose = props.onClose;
  var actions = props.actions || [];

  var isOpen = medium ? true : false;
  useEffect(function() {
    if (!isOpen) {
      document.documentElement.style.overflow = '';
      return;
    }
    document.documentElement.style.overflow = 'hidden';
    return function() {
      document.documentElement.style.overflow = '';
    };
  }, [isOpen]);

  if (!medium) return null;

  var avatarStyle = getAvatarStyle(medium.screen_name);
  var status = getStatusLabel(medium.status_name);

  var general = details && details.general ? details.general : null;

  return ReactDOM.createPortal(
    <div>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.25)',
          zIndex: 9998,
        }}
      />

      {/* Slide-in panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100%',
          width: window.innerWidth <= 600 ? '100vw' : '360px',
          maxWidth: '100vw',
          background: '#fff',
          zIndex: 9999,
          boxShadow: '-6px 0 32px rgba(0,0,0,0.14)',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Header */}
        <div style={{ background: '#f8f4f0', padding: '18px 20px 14px', borderBottom: '1px solid #e8ddd6', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={medium.profile_image ? {
              width: 46,
              height: 46,
              borderRadius: '50%',
              overflow: 'hidden',
              flexShrink: 0,
            } : Object.assign({}, avatarStyle, {
              width: 46,
              height: 46,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '700',
              fontSize: '17px',
              flexShrink: 0,
            })}>
              {medium.profile_image ? (
                <img
                  src={process.env.REACT_APP_IMAGE_URL + medium.profile_image}
                  alt={medium.screen_name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                />
              ) : getInitials(medium.screen_name)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#d5a67b', marginBottom: '2px' }}>
                Medium Information
              </div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: '#485257', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {medium.screen_name || 'N/A'}
              </div>
              <div style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {medium.email}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '22px', lineHeight: 1, padding: '4px', flexShrink: 0, alignSelf: 'flex-start' }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Info section */}
        <div style={{ padding: '4px 20px 8px', flex: 1 }}>
          <InfoRow label="Approved Role" value={getMediumRole(medium.current_mediumship_level)} />
          <InfoRow
            label="Status"
            value={
              <span style={{ background: status.bg, color: status.color, padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                {status.label}
              </span>
            }
          />
          <InfoRow label="Online Status" value={medium.is_available ? 'Online' : 'Offline'} />
          <InfoRow label="Test User" value={medium.test_user === 1 ? 'On' : 'Off'} />
          <InfoRow label="Starter Referral Code" value={medium.refer_code || '-'} />
          <InfoRow label="Total Starter Referrals" value={medium.number_of_uses !== undefined ? String(medium.number_of_uses) : '-'} />
          {detailsLoading ? (
            <div style={{ padding: '10px 0', fontSize: '12px', color: '#bbb' }}>Loading details…</div>
          ) : general ? (
            <div>
              {general.phone && <InfoRow label="Phone" value={general.phone} />}
              {general.whatsapp && <InfoRow label="WhatsApp" value={general.whatsapp} />}
              {general.country && <InfoRow label="Country" value={general.country} />}
              {(general.timezone_label || general.timezone) && <InfoRow label="Timezone" value={general.timezone_label || general.timezone} />}
              {general.created_at && <InfoRow label="Registered" value={general.created_at} />}
            </div>
          ) : null}
          {(medium.timezone_label || medium.timezone) && !detailsLoading && !general && <InfoRow label="Timezone" value={medium.timezone_label || medium.timezone} />}
          {medium.created_at && !detailsLoading && !general && <InfoRow label="Registered" value={medium.created_at} />}
          <InfoRow
            label="Zoom"
            value={
              <span style={{ background: medium.zoom_integration ? '#eafaf1' : '#fdf0ef', color: medium.zoom_integration ? '#27ae60' : '#e74c3c', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                {medium.zoom_integration ? 'Connected' : 'Not Connected'}
              </span>
            }
          />
          <InfoRow
            label="Google Calendar"
            value={
              <span style={{ background: medium.google_calendar_integration ? '#eafaf1' : '#fdf0ef', color: medium.google_calendar_integration ? '#27ae60' : '#e74c3c', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                {medium.google_calendar_integration ? 'Connected' : 'Not Connected'}
              </span>
            }
          />
          {/* pay_forward is the Share it Forward master toggle
              (user_preference_pages.pay_forward). Every admin medium list row
              already carries it — the backend User model appends it — so it
              needs no per-list API change. It arrives as 0/1, not a boolean.
              "Off" is deliberately neutral grey rather than the red used for
              Zoom/Google Calendar: opting out is a valid choice, not a broken
              integration. */}
          <InfoRow
            label="Share it Forward"
            value={
              <span style={{ background: medium.pay_forward ? '#eafaf1' : '#f5f5f5', color: medium.pay_forward ? '#27ae60' : '#888', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                {medium.pay_forward ? 'Active' : 'Off'}
              </span>
            }
          />
          <InfoRow label="Slots Last Generated" value={medium.slots_last_generated_at} />
          {medium.payment_integration && <InfoRow label="Payment" value={medium.payment_integration} />}
          {medium.comments && (
            <div style={{ padding: '9px 0', borderBottom: '1px solid #f0ebe5' }}>
              <span style={{ fontSize: '12px', color: '#999', fontWeight: '500', display: 'block', marginBottom: '6px' }}>Comments</span>
              <div style={{ background: '#faf7f4', border: '1px solid #f0ebe5', borderRadius: '6px', padding: '8px 10px', maxHeight: '120px', overflowY: 'auto', fontSize: '13px', color: '#485257', fontWeight: '500', wordBreak: 'break-word' }}>
                {medium.comments.split('\n').map(function(line, i) {
                  return <div key={i}>{line || <br />}</div>;
                })}
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        {actions.length > 0 && (
          <div style={{ padding: '14px 20px 20px', borderTop: '1px solid #f0ebe5', flexShrink: 0, display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {actions.map(function(action, i) {
              var style = getButtonStyle(action.variant);
              if (action.disabled) {
                style = Object.assign({}, style, { opacity: 0.6, cursor: 'not-allowed' });
              }
              return (
                <button key={i} onClick={action.onClick} disabled={action.disabled} style={style}>
                  {action.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
